import React, { useEffect, useState } from "react"
import Page from "../../common/page/Page"
import axios from "axios"
import ButtonArea from "../../common/ButtonArea/ButtonArea"
import Form from "../../common/form/Form"
import ResponsiveTable from "../../common/ResponsiveTable/ResponsiveTable"
import EditIcon from "../../icons/EditIocn"
import DoubleArrowIcon from "../../icons/DoubleArrowIcon"
import Loader from "../../common/loader/Loader"
import ToggleOffIcon from "../../icons/ToggleOffIcon"
import ToggleOnIcon from "../../icons/ToggleOnIcon"
import DeleteIcon from "../../icons/DeleteIcon"
import { useAuth } from "../../data/useAuth"

const DeliveryStatePage = () => {
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formActive, setFormActive] = useState(false)
  const [isAdd, setIsAdd] = useState(true)
  const { signOut } = useAuth()
  const [formData, setFormData] = useState({
    name: "",
    delivery_rate: 0,
    additional_rate: 0,
  })

  useEffect(() => {
    fetchStates()
  }, [])

  const fetchStates = async () => {
    try {
      const response = await axios.get("/api/delivery-states/all")
      setStates(response.data || [])
    } catch (err) {
      setError(err.response?.data?.message || err.message)
      signOut()
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      alert("State name is required.")
      return
    }
    if (formData.delivery_rate === "" || isNaN(formData.delivery_rate)) {
      alert("Delivery rate is required and must be a number.")
      return
    }

    try {
      setLoading(true)
      setFormActive(false)
      if (isAdd) {
        const response = await axios.post(
          "/api/delivery-states/create",
          formData
        )
        setStates((prev) => [...prev, response.data])
      } else {
        await axios.put(`/api/delivery-states/update/${formData.id}`, formData)
        setStates((prev) =>
          prev.map((state) =>
            state.id === formData.id ? { ...state, ...formData } : state
          )
        )
      }

      // setFormActive(false)
      resetForm()
      setLoading(false)
    } catch (err) {
      setFormActive(false)
      setLoading(false)
      setError(err.response?.data?.error || err.message)
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      delivery_rate: 0,
      additional_rate: 0,
    })
    setIsAdd(true)
  }

  const handleEdit = (state) => {
    setFormActive(true)
    setIsAdd(false)
    setFormData({
      id: state.id,
      name: state.name,
      delivery_rate: state.delivery_rate,
      additional_rate: state.additional_rate,
    })
  }

  const handleActivate = async (id) => {
    try {
      await axios.patch(`/api/delivery-states/activate/${id}`)
      setStates((prev) =>
        prev.map((state) =>
          state.id === id ? { ...state, is_active: true } : state
        )
      )
    } catch (err) {
      alert(err.response?.data?.error || "Failed to activate")
    }
  }

  const handleDeactivate = async (id) => {
    try {
      await axios.patch(`/api/delivery-states/deactivate/${id}`)
      setStates((prev) =>
        prev.map((state) =>
          state.id === id ? { ...state, is_active: false } : state
        )
      )
    } catch (err) {
      alert(err.response?.data?.error || "Failed to deactivate")
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this state?")) return

    try {
      await axios.delete(`/api/delivery-states/delete/${id}`)
      setStates((prev) => prev.filter((state) => state.id !== id))
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete")
    }
  }

  return (
    <Page title={"Delivery"} is_admin={true}>
      {!formActive && (
        <ButtonArea>
          <button
            onClick={() => {
              setFormActive(true)
              setIsAdd(true)
              resetForm()
            }}
          >
            Add State
          </button>
        </ButtonArea>
      )}

      {formActive && (
        <Form is_admin={true} onSubmit={handleSubmit}>
          <div>
            <div>
              <label htmlFor="name">State Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={isAdd ? handleInputChange : ""}
                placeholder="State Name"
                required
              />
            </div>
          </div>
          <div>
            <div>
              <label htmlFor="delivery_rate">Delivery Rate ₹</label>
              <input
                type="number"
                id="delivery_rate"
                value={formData.delivery_rate}
                onChange={handleInputChange}
                placeholder="Delivery Rate"
                required
              />
            </div>
          </div>
          <div>
            <div>
              <label htmlFor="additional_rate">Additional Rate ₹</label>
              <input
                type="number"
                id="additional_rate"
                value={formData.additional_rate}
                onChange={handleInputChange}
                placeholder="Additional Rate"
                required
              />
            </div>
          </div>

          <ButtonArea>
            <button type="submit">{isAdd ? "Add" : "Update"} State</button>
            <button
              type="button"
              onClick={() => {
                setFormActive(false)
                resetForm()
              }}
            >
              Close
            </button>
          </ButtonArea>
        </Form>
      )}

      {loading && (
        <div style={{ height: "calc(100svh - 100px)", display: "flex" }}>
          <div style={{ margin: "auto" }}>
            <Loader />
          </div>
        </div>
      )}

      {!loading && !error && !formActive && (
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th>Rate ₹</th>
              <th>Additional Rate ₹</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {states.map((state) => (
              <tr key={state.id}>
                <td data-label="Id">{state.id}</td>
                <td data-label="Name">{state.name}</td>
                <td data-label="Rate ₹">{state.delivery_rate}</td>
                <td data-label="Additional Rate ₹">{state.additional_rate}</td>
                <td data-label="Status">
                  {!state.is_active ? (
                    <button
                      onClick={() => handleActivate(state.id)}
                      style={{
                        marginLeft: "5px",
                        padding: "3px 6px",
                        fill: "red",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <ToggleOffIcon />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDeactivate(state.id)}
                      style={{
                        marginLeft: "5px",
                        padding: "3px 6px",
                        fill: "green",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <ToggleOnIcon />
                    </button>
                  )}
                </td>
                <td data-label="Actions">
                  <button
                    onClick={() => handleEdit(state)}
                    style={{
                      padding: "3px 6px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <EditIcon />
                  </button>
                  <button
                    onClick={() => handleDelete(state.id)}
                    style={{
                      padding: "3px 6px",
                      border: "none",
                      background: "none",
                      color: "red",
                      cursor: "pointer",
                    }}
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
    </Page>
  )
}

export default DeliveryStatePage
